import { AuthStatus, DbRef } from '@caresend/types';
import { getUnknownErrorMessage } from '@caresend/utils';
import { createUserWithEmailAndPassword, updatePassword as updatePassword$1, signInWithEmailAndPassword } from 'firebase/auth';
import { checkEmailAlreadyInUseRequest } from './cloudFunctions.js';
import { firebaseAuth } from './init.js';
import { dbSet } from './methods.js';
import { reportException } from '../../functions/sentry.js';
import { toastError, toastSuccess, toastResolve, toastReject } from '../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../components/ui/Icon.vue.js';
import '../../functions/configuration/icons.js';
import '../algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../functions/tracking/index.js';
import { trackDebugEvent } from '../../functions/tracking/tracking.js';
import { getStore } from '../../store/helpers/initStore.js';

/**
 * Wrapper for Firebase’s sign in method to add support for try/catch.
 * https://stackoverflow.com/questions/51819349/getting-uncaught-after-catching-error-in-firebase-auth-with-async
 */
const firebaseSignInWithEmailAndPassword = async (email, password) => new Promise((resolve, reject) => {
    signInWithEmailAndPassword(firebaseAuth, email, password)
        .then((userCredential) => resolve(userCredential))
        .catch((err) => reject(err));
});
const signIn = async (email, password) => {
    try {
        const { user } = await firebaseSignInWithEmailAndPassword(email, password);
        if (!(user === null || user === void 0 ? void 0 : user.uid))
            throw new Error('Sign in failed.');
        return true;
    }
    catch (error) {
        if (getStore().state.auth.authStatus !== AuthStatus.SIGNED_OUT) {
            firebaseAuth.signOut();
        }
        const originalMessage = getUnknownErrorMessage(error);
        let message = originalMessage;
        if (originalMessage.includes('auth/wrong-password')) {
            message = 'Incorrect password';
        }
        else if (originalMessage.includes('auth/network-request-failed')) {
            message = 'Failed to connect';
        }
        throw toastError(message);
    }
};
const signOut = async () => {
    try {
        await firebaseAuth.signOut();
        toastSuccess('Successfully logged out');
        return true;
    }
    catch (error) {
        throw toastError(error);
    }
};
const checkEmailAlreadyInUse = async (request) => {
    const { isInUse } = await checkEmailAlreadyInUseRequest(request);
    return { isInUse, email: request.email };
};
const registerUser = (user, password) => new Promise((resolve, reject) => {
    createUserWithEmailAndPassword(firebaseAuth, user.info.email, password)
        .then((userCreated) => {
        var _a;
        const id = (_a = userCreated.user) === null || _a === void 0 ? void 0 : _a.uid;
        const userWithId = { ...user, id };
        dbSet(`${DbRef.USERS}/${id}`, userWithId);
        if (typeof id === 'string') {
            toastResolve(resolve, 'User created!', id);
        }
    })
        .catch((error) => toastReject(reject, error));
});
const updatePassword = async (userID, password) => {
    const { currentUser } = firebaseAuth;
    if (!currentUser) {
        throw Error('Not signed in');
    }
    try {
        await updatePassword$1(currentUser, password);
    }
    catch (error) {
        // Temporary tracking for password update errors
        trackDebugEvent('updatePassword', { error });
        reportException(error);
        throw error;
    }
    await dbSet(`${DbRef.USERS}/${userID}/passwordCreated`, true);
};

export { checkEmailAlreadyInUse, registerUser, signIn, signOut, updatePassword };
